import { withProps, withRef } from "@udecode/cn";
import { PluginConfig, TElement } from "@udecode/plate-common";
import {
    ParagraphPlugin as BaseParagraphPlugin,
    BoldPlugin,
    Plate,
    PlateElement,
    PlateLeaf,
    createPlatePlugin,
    createTPlatePlugin,
    usePlateEditor,
} from "@udecode/plate/react";
import { BoldIcon, Heading1 } from "lucide-react";
import { useMemo, useRef } from "react";

import {
    ExplanationElement,
    ExplanationInputElement,
    ExplanationInputPlugin,
    ExplanationPlugin,
} from "@/components/RichTextEditor/ExplanationPlugin";
import ReplacementPlugin from "@/components/RichTextEditor/TextReplacementPlugin";
import ToggleBlockToolbarButton from "@/components/RichTextEditor/ToggleBlockToolbarButton";
import { Editor, EditorContainer } from "@/components/plate-ui/editor";
import { FixedToolbar } from "@/components/plate-ui/fixed-toolbar";
import { MarkToolbarButton } from "@/components/plate-ui/mark-toolbar-button";
import { ParagraphElement } from "@/components/plate-ui/paragraph-element";
import { ToolbarGroup } from "@/components/plate-ui/toolbar";
import { GameMetadata } from "@/gen/realtime";
import { FeedRecordPreset, recordTypes, zFeedRecordRichText } from "@/routes/game-feed/types";
import { cn } from "@/utils";

import FeedRecordTypeDropdownMenu from "./FeedRecordTypeDropdownMenu";

const EMPTY_EDITOR_CONTENT = zFeedRecordRichText.parse([
    {
        type: "paragraph",
        children: [{ text: "" }],
    },
]);

const wordReplacements: Record<string, string> = {
    Bi: "Bishop",
    P: "Pawn",
    N: "Knight",
    K: "King",
    Q: "Queen",
    R: "Rook",
};

const ParagraphPlugin = BaseParagraphPlugin.configure({
    node: {
        isElement: true,
        type: "paragraph",
        component: ParagraphElement,
    },
});

const HeadingPlugin = createPlatePlugin({
    key: "heading",
    node: {
        isElement: true,
        type: "heading",
        component: withProps(PlateElement, {
            as: "h1",
            className: "font-planar15 text-lg",
        }),
    },
    shortcuts: {
        toggleHeading: {
            keys: ["Mod+Shift+1", "Mod+Alt+1"],
            handler: ({ editor }) => {
                editor.tf.toggle.block({ type: "heading" });
            },
        },
    },
});

export const SuperHeadingElement = withRef<typeof PlateElement>(
    ({ children, className, getOption, getOptions, ...props }, ref) => {
        const color = getOption("color");

        return (
            <PlateElement
                ref={ref}
                as="h1"
                className={cn("font-planar30 text-xl uppercase", className)}
                style={{ color }}
                getOption={getOption}
                getOptions={getOptions}
                {...props}
            >
                {children}
            </PlateElement>
        );
    }
);

type SuperHeadingConfig = PluginConfig<"super-heading", { color?: string }>;
const BaseSuperHeadingPlugin = createTPlatePlugin<SuperHeadingConfig>({
    key: "super-heading",
    options: {
        color: undefined,
    },
    node: {
        isElement: true,
        type: "super-heading",
        component: SuperHeadingElement,
    },
});

type FeedRichTextEditorProps = {
    initialValue?: TElement[] | null;
    onChange: (value: TElement[]) => void;
    preset?: FeedRecordPreset | null;
    onPresetChange?: (preset: FeedRecordPreset | null) => void;
    game?: GameMetadata;
};
export default function FeedRichTextEditor({
    initialValue,
    onChange,
    preset,
    onPresetChange,
    game,
}: FeedRichTextEditorProps) {
    const FeedReplacementsPlugin = useMemo(
        () =>
            ReplacementPlugin.configure({
                options: {
                    replacements: {
                        ...wordReplacements,
                        ...(game
                            ? {
                                  W: game.whitePlayer.nickname,
                                  B: game.blackPlayer.nickname,
                              }
                            : {}),
                    },
                },
            }),
        [game]
    );

    const recordType = preset ? recordTypes[preset] : null;
    const editor = usePlateEditor({
        value: initialValue ?? EMPTY_EDITOR_CONTENT,
        plugins: [
            ParagraphPlugin,
            HeadingPlugin,
            BaseSuperHeadingPlugin.configure({ options: { color: recordType?.background } }),
            BoldPlugin,
            FeedReplacementsPlugin,
            ExplanationInputPlugin,
            ExplanationPlugin,
        ],
        override: {
            components: {
                bold: withProps(PlateLeaf, { as: "strong" }),
                [ExplanationPlugin.key]: ExplanationElement,
                [ExplanationInputPlugin.key]: ExplanationInputElement,
            },
        },
    });

    const previousPreset = useRef(preset);
    if (preset !== previousPreset.current) {
        previousPreset.current = preset;

        const firstNode = editor.children[0];
        if (firstNode.type === "super-heading") {
            editor.removeNodes({ at: [0] });
        }

        if (preset && recordTypes[preset]) {
            editor.insertNodes(recordTypes[preset].content, { at: [0] });
        }
    }

    return (
        <Plate editor={editor} onChange={({ value }) => onChange?.(value)} data-registry="plate">
            <EditorContainer variant="default">
                <FixedToolbar>
                    <ToolbarGroup>
                        <ToggleBlockToolbarButton blockType="heading" tooltip="Heading (⌘+⇧+1)">
                            <Heading1 />
                        </ToggleBlockToolbarButton>
                        <MarkToolbarButton nodeType={BoldPlugin.key} tooltip="Bold (⌘+B)">
                            <BoldIcon />
                        </MarkToolbarButton>
                    </ToolbarGroup>
                    {preset !== undefined ? (
                        <ToolbarGroup>
                            <FeedRecordTypeDropdownMenu
                                value={preset ?? null}
                                onValueChange={preset => onPresetChange?.(preset)}
                            />
                        </ToolbarGroup>
                    ) : null}
                </FixedToolbar>
                <Editor
                    className="px-4 sm:px-4"
                    data-record-type={preset}
                    style={{ background: "#1E1E1E", color: "#f4f4f4" }}
                />
            </EditorContainer>
        </Plate>
    );
}
